import { HttpErrorResponse } from '@angular/common/http';

/** The server-recognised MFA action names (exact strings the API expects
 *  in the challenge body). Multiple endpoints can map to the same action. */
export type MfaAction = 'PayrollRun' | 'PayrollConfirm' | 'BankAccountChange' | 'PayrollReset';

// Error codes returned in the envelope `code` field on MFA failures.
export const MFA_TOKEN_REQUIRED    = 'MFA_TOKEN_REQUIRED';
export const MFA_TOKEN_INVALID     = 'MFA_TOKEN_INVALID';
export const MFA_CHALLENGE_EXPIRED = 'MFA_CHALLENGE_EXPIRED';
export const MFA_OTP_INVALID       = 'MFA_OTP_INVALID';
export const MFA_PHONE_MISSING     = 'MFA_PHONE_MISSING';

/** Header that carries the single-use token on the retried protected request. */
export const X_MFA_TOKEN = 'X-Mfa-Token';

/** Modal title per action. */
export const MFA_ACTION_LABELS: Record<MfaAction, string> = {
  PayrollRun:        'Confirm payroll run',
  PayrollConfirm:    'Confirm payroll approval',
  BankAccountChange: 'Confirm bank account change',
  PayrollReset:      'Confirm payroll reset'
};

/** Result of requesting a code (`POST /api/mfa/challenge`). */
export type ChallengeResult =
  | { kind: 'sent';          challengeId: string }
  | { kind: 'phone-missing'; message: string }
  | { kind: 'unavailable';   message: string };

/** Result of submitting a code (`POST /api/mfa/challenge/{id}/verify`). */
export type VerifyResult =
  | { kind: 'verified';    mfaToken: string }
  | { kind: 'incorrect';   message: string }
  | { kind: 'expired';     message: string }
  | { kind: 'locked';      message: string }
  | { kind: 'unavailable'; message: string };

/**
 * Thrown when the user dismisses the MFA modal without completing it. The
 * original protected request fails with this so call-site error handlers can
 * render a clean message ("Verification cancelled.") instead of the raw 403.
 */
export class MfaCancelledError extends Error {
  constructor(message = 'Verification cancelled.') {
    super(message);
    this.name = 'MfaCancelledError';
  }
}

/**
 * Extract an EmitRoll/ASP.NET error code from a 4xx body, tolerant of shape
 * (`error.data.code`, top-level `error.code`, or a JSON-string body). Mirrors
 * the defensive parsing in `isPhoneVerificationRequired`.
 */
export function mfaErrorCode(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) { return null; }
  let body: unknown = err.error;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { return null; }
  }
  const b = body as { data?: { code?: string }; code?: string } | null;
  return b?.data?.code ?? b?.code ?? null;
}

/**
 * Map a protected request (method + URL) to its MFA action, or null when the
 * request is not one of the gated operations — so unrelated 403s bubble
 * untouched. `[^/]+` segments match a single path component (an id), which is
 * why `bank-accounts/{id}` (delete) does not also catch
 * `bank-accounts/{id}/set-default`.
 */
export function resolveMfaAction(method: string, url: string): MfaAction | null {
  const m = method.toUpperCase();
  if (m === 'POST'   && /\/api\/payroll\/run(?:$|\?)/.test(url))                                 { return 'PayrollRun'; }
  if (m === 'POST'   && /\/api\/payroll\/[^/]+\/confirm(?:$|\?)/.test(url))                      { return 'PayrollConfirm'; }
  if (m === 'POST'   && /\/api\/payroll\/[^/]+\/reset(?:$|\?)/.test(url))                        { return 'PayrollReset'; }
  if (m === 'POST'   && /\/api\/employee\/bank-accounts(?:$|\?)/.test(url))                      { return 'BankAccountChange'; }
  if (m === 'PUT'    && /\/api\/employee\/bank-accounts\/[^/]+\/set-default(?:$|\?)/.test(url))  { return 'BankAccountChange'; }
  if (m === 'DELETE' && /\/api\/employee\/bank-accounts\/[^/]+(?:$|\?)/.test(url))               { return 'BankAccountChange'; }
  return null;
}
